/**
 * Standard type definitions for all selector modals
 *
 * This file establishes the architectural standards for selector components,
 * ensuring consistency across all domain-specific implementations.
 */

import { Colors } from '@/api';
import type { Ref } from 'vue';

/**
 * Standard props interface for single-select modals
 *
 * IMPORTANT: All single selectors accept full objects as values.
 * This matches consumer usage and prevents unnecessary ID extraction.
 * Domain services handle ID conversion internally.
 */
export interface StandardSingleSelectorProps<T> {
  // Value handling - accepts full objects
  value?: T | null;

  // Display configuration
  title?: string;
  primaryColor?: Colors;

  // Clear functionality
  clearable?: boolean;
  clearText?: string;

  // Action configuration
  actionText?: string;
  closeOnSelect?: boolean;

  // No-item option (standardized naming)
  noItemOption?: boolean;
  noItemText?: string;

  // Archive functionality (standardized naming)
  showArchivedToggle?: boolean;
}

/**
 * Standard props interface for multi-select modals
 *
 * IMPORTANT: Multi selectors accept arrays of full objects as values.
 * This matches consumer usage and prevents unnecessary ID extraction.
 * Domain services handle ID conversion internally.
 */
export interface StandardMultiSelectorProps<T> {
  // Value handling - accepts full objects
  value?: T[];

  // Display configuration
  title?: string;
  primaryColor?: Colors;

  // Clear functionality
  clearable?: boolean;
  clearText?: string;

  // Action configuration
  actionText?: string;

  // No-item option (standardized naming)
  noItemOption?: boolean;
  noItemText?: string;

  // Archive functionality (standardized naming)
  showArchivedToggle?: boolean;
}

/**
 * Standard events emitted by single-select modals
 *
 * Event contract ensures consistent parent-child communication
 */
export interface StandardSingleSelectorEmits<T> {
  // Primary v-model event - emits the selected object
  (e: 'update:value', item: T | null): void;

  // Selection confirmation - emits the selected object
  (e: 'selected', item: T): void;

  // Clear action
  (e: 'cleared'): void;

  // No-item selection
  (e: 'no-item-selected'): void;

  // Lifecycle events
  (e: 'show'): void;
  (e: 'hide'): void;
}

/**
 * Standard events emitted by multi-select modals
 *
 * IMPORTANT: Multi selectors emit full objects for all events
 * to match the object-based props pattern
 */
export interface StandardMultiSelectorEmits<T> {
  // Primary v-model event - emits full objects
  (e: 'update:value', items: T[]): void;

  // Selection confirmation - emits full objects
  (e: 'selected', items: T[]): void;

  // Clear action
  (e: 'cleared'): void;

  // No-item selection
  (e: 'no-item-selected'): void;

  // Lifecycle events
  (e: 'show'): void;
  (e: 'hide'): void;
}

/**
 * Standard props interface for ALL browser components
 *
 * CRITICAL: Browser components MUST use generic prop names, NOT entity-specific names.
 * This ensures consistency across all entity selectors and reduces cognitive load.
 *
 * ❌ WRONG (entity-specific):
 *   - selectedUser, selectedUsers (UserBrowser)
 *   - selectedLabel, selectedLabels (LabelBrowser)
 *   - selectedTeam, selectedTeams (TeamBrowser)
 *
 * ✅ CORRECT (generic):
 *   - selectedItem (single mode)
 *   - selectedItems (multi mode)
 *   - selectedIds (optional, for ID-based comparison)
 *
 * NOTE: "No item" option (empty selection) should be handled by the Selector
 * component, NOT the Browser. Browsers only manage entity selection.
 */
export interface StandardBrowserProps<T> {
  // Selection mode
  mode: 'single' | 'multi';

  // Selection - Use generic names ONLY
  selectedItem?: T | null; // Single mode: the selected object
  selectedItems?: T[]; // Multi mode: array of selected objects
  selectedIds?: string[]; // Optional: for ID-based comparison (both modes)

  // Display configuration
  primaryColor?: Colors;

  // Filtering
  includeArchived?: boolean; // NOT: includeInactive, showArchived
}

/**
 * Standard events emitted by browser components
 *
 * Events use generic names to match the generic prop pattern.
 */
export interface StandardBrowserEmits<T> {
  // Selection events - generic names
  (e: 'item-selected', item: T): void; // NOT: user-selected, label-selected
  (e: 'item-toggled', item: T, selected: boolean): void;
  (e: 'selection-changed', items: T[]): void;

  // Archive toggle (if browser has its own toggle)
  (e: 'toggle-archived', value: boolean): void;
}

/**
 * Standard browser instance interface
 *
 * All browser components should expose these methods for consistent
 * interaction with selector templates
 */
export interface StandardBrowserInstance<T> {
  // Initialization and data loading
  initialize(params?: Record<string, unknown>): Promise<void>;

  // Search functionality
  search(query: string): void;
  setSearchQuery(query: string): void;
  getSearchResultsCount?(): number;

  // Focus management
  handleFocusFirst(): void;
  handleFocusLast(): void;
  handleFocusLastFromFooter?(): void;

  // Selection management
  scrollToFirstSelected(): Promise<boolean | undefined>;
  getFocusedItem?(): T | 'no-item' | null;

  // Hierarchical navigation (optional)
  navigateToParent?(): void;
  navigateToChild?(): void;
}

/**
 * Standard selector template instance interface
 *
 * All selector templates should expose these methods for external control
 */
export interface StandardSelectorInstance {
  showPopup(trigger?: HTMLElement): void;
  closePopup(): void;
  clearSearch?(): void;
  focusHeader?(): void;
  isVisible: boolean;
  updateSelectAllState?: (state: 'all' | 'some' | 'none') => void;
}

/**
 * Configuration for browser initialization
 *
 * Used with useEnhancedModalLifecycle for consistent initialization
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface BrowserInitConfig<T> {
  browserRef: Ref<StandardBrowserInstance<T> | null>;
  getInitParams: () => Record<string, unknown>;
  onInitialized?: () => Promise<void>;
  onError?: (error: Error) => void;
}

/**
 * Standard domain service interface
 *
 * All domain services should implement these methods for consistency
 */
export interface StandardDomainService<T> {
  // Display helpers
  getDisplayName(item: T): string;
  getItemId(item: T): string;

  // Validation
  isValidId(id: string): boolean;

  // Cache operations
  findInCache(cache: T[], id: string): T | undefined;

  // Domain-specific helpers (optional)
  isArchived?(item: T): boolean;
  getParentId?(item: T): string | undefined;
  getChildren?(item: T, allItems: T[]): T[];
}

/**
 * Naming conventions to ensure consistency
 * Exported as value (not type) so it can be used in templates
 */
export const STANDARD_NAMING = {
  // Selector Props (wrapper modal components)
  NO_ITEM_OPTION: 'noItemOption', // NOT: showNoItemOption, hideNoItemOption
  NO_ITEM_TEXT: 'noItemText', // NOT: noUserText, noBoardText
  SHOW_ARCHIVED: 'showArchivedToggle', // NOT: showInactiveToggle
  INCLUDE_ARCHIVED: 'includeArchived', // NOT: includeInactive, includeArchivedItems

  // Browser Props (CRITICAL: Use generic names, NOT entity-specific)
  // ❌ WRONG: selectedUser, selectedUsers, selectedLabel, selectedLabels, etc.
  // ✅ CORRECT: selectedItem, selectedItems, selectedIds
  SELECTED_ITEM: 'selectedItem', // NOT: selectedUser, selectedLabel, selectedTeam, etc.
  SELECTED_ITEMS: 'selectedItems', // NOT: selectedUsers, selectedLabels, selectedTeams, etc.
  SELECTED_IDS: 'selectedIds', // For ID-based selection (both modes)

  // CSS classes
  CONTENT_ITEM: 'item-selector__content-item',
  CONTENT_WRAPPER: 'item-selector__content-wrapper',

  // Common text
  CLEAR_TEXT: 'Clear',
  CANCEL_TEXT: 'Cancel',
  SELECT_TEXT: 'Select',
} as const;

/**
 * Helper type for extracting item ID type from domain object
 */
export type ItemId<T> = T extends { id: infer ID } ? ID : string;

/**
 * Helper type for value prop based on selector mode
 */
export type SelectorValue<T, Multi extends boolean> = Multi extends true
  ? T[]
  : T | null;
